import React, { useCallback, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import { useLocation, useNavigate } from "react-router-dom";
import { drawerPaperSx } from "./drawerStyles";
import { DRAWER_WIDTH } from "../../constants/layout";
import { resetState } from "../../redux/auth/authSlice";
import { canAccessSuperAdminFeatures } from "../../constants/adminAccess";
import authService from "../../redux/auth/authService";
import { useDispatch, useSelector } from "react-redux";
import { getUnlistedOrders } from "../../redux/rm/rmSlice";
import SidebarIconItem from "./SidebarIconItem";
import {
  bottomNavItems,
  collectNavItemPaths,
  getAdminNavItems,
  getDefaultNavItems,
  resolveActiveNavPath,
} from "./sidebarNavConfig";

const DrawerLeft: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const userType = localStorage?.getItem("userType");
  const showSuperAdminDashboard = canAccessSuperAdminFeatures(
    authService.getAumUserId()
  );

  const orders = useSelector((state: any) => state?.rm?.orders);
  const pendingOrdersCount =
    orders?.filter((order: any) => order.status === "PENDING")?.length || 0;

  const fetchOrders = useCallback(async () => {
    if (userType === "4") {
      await dispatch(
        getUnlistedOrders({
          rmId: 0,
          status: "PENDING",
          type: "ALL",
          searchQuery: "",
          startDate: null,
          endDate: null,
          pageIndex: 1,
          pageSize: 10000,
        })
      );
    }
  }, [dispatch, userType]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleLogout = () => {
    authService.logout();
    dispatch(resetState());
    navigate("/");
  };

  const navItems = useMemo(() => {
    if (userType === "4") {
      return getAdminNavItems(showSuperAdminDashboard, pendingOrdersCount);
    }
    return getDefaultNavItems(userType, pendingOrdersCount);
  }, [userType, showSuperAdminDashboard, pendingOrdersCount]);

  const activePath = useMemo(() => {
    const paths = [
      ...collectNavItemPaths(navItems),
      bottomNavItems.settings.to,
    ];
    return resolveActiveNavPath(location.pathname, paths);
  }, [location.pathname, navItems]);

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <CssBaseline />
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": drawerPaperSx,
        }}
        variant="persistent"
        anchor="left"
        open
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            bgcolor: "#000000",
            width: DRAWER_WIDTH,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pt: 1.5,
              pb: 1,
              "&::-webkit-scrollbar": { width: 0 },
            }}
          >
            {navItems.map((item) => (
              <SidebarIconItem
                key={item.id}
                label={item.label}
                icon={item.icon}
                to={item.to}
                children={item.children}
                sections={item.sections}
                activePath={activePath}
              />
            ))}
          </Box>

          <Box
            sx={{
              borderTop: "1px solid rgba(241, 235, 235, 0.12)",
              py: 1,
              pb: 2,
            }}
          >
            <SidebarIconItem
              label={bottomNavItems.settings.label}
              icon={bottomNavItems.settings.icon}
              to={bottomNavItems.settings.to}
              activePath={activePath}
            />
            <SidebarIconItem
              label={bottomNavItems.logout.label}
              icon={bottomNavItems.logout.icon}
              onClick={handleLogout}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DrawerLeft;
